import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import type { VirtualAppointmentSummary } from '@odo/shared-ui/clinical/virtualAppointments/summary';

const MAX_LISTED_DUE_NAMES = 3;

type DueSource = Pick<VirtualAppointmentDto, 'status' | 'isDue'>;

// Open-now runs the trigger, which also opens every planned VA already due; a missing flag is not due.
export function selectDueVirtualAppointments<T extends DueSource>(vas: readonly T[]): T[] {
    return vas.filter((va) => va.status === 'planned' && va.isDue === true);
}

// Outside Tx-0 to Tx-3 the trigger is a no-op, so nothing already due opens on submit.
export function selectDueOnSubmit<T extends DueSource>(
    planned: readonly T[],
    canOpenNow: boolean
): T[] {
    return canOpenNow ? selectDueVirtualAppointments(planned) : [];
}

export function dueVaName(
    summary: Pick<VirtualAppointmentSummary, 'typeTitle' | 'typeLabel'>
): string {
    return summary.typeLabel ?? summary.typeTitle;
}

// "A, B, C +2 more"
export function formatDueNames(names: readonly string[]): string {
    const listed = names.slice(0, MAX_LISTED_DUE_NAMES).join(', ');
    const rest = names.length - MAX_LISTED_DUE_NAMES;
    return rest > 0 ? `${listed} +${rest} more` : listed;
}

export function dueNoticeText(names: readonly string[]): string | null {
    if (names.length === 0) return null;
    const count =
        names.length === 1 ? '1 planned VA that is' : `${names.length} planned VAs that are`;
    return `Also opens ${count} already due: ${formatDueNames(names)}`;
}

// The notice describes what Submit does, so it stays hidden while there is nothing to submit.
export function openNowDueNotice(names: readonly string[], canSubmit: boolean): string | null {
    return canSubmit ? dueNoticeText(names) : null;
}
